// Summary: Orders list table for POS with selection state and transfer-aware affordances.
// Details: docs/function-library/web/contexts-pages.md#pages-posshellpage-components-orderssection
import React, { useEffect, useMemo, useState } from 'react';
import { Button, Modal, TextInput, UnstyledButton } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight, faChevronLeft, faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';
import { faFileLines } from '@fortawesome/free-regular-svg-icons';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import type { OrderType, OrderView } from '../../../../../services/orders/orders.service';
import { subscribeSharedTicker } from '../../../../../utils/shared-ticker';
import { formatMoney } from '../../../../../utils/money';
import { isQuickChargeOrderCode, resolveTip } from 'shared-models/browser';
import classes from './orders-table.module.scss';

type TypeFilterKey = OrderType | 'all' | 'quick_charge';

export type OrdersTableProps = {
    orders: OrderView[];
    selectedOrderId?: number | null;
    onSelectOrder: (order: OrderView) => void;
    filterSlot?: React.ReactNode;
    // History reuse: hide the dine-in/take-away pills, override the search hint + empty message.
    hideTypeFilter?: boolean;
    searchPlaceholder?: string;
    emptyMessage?: string;
    // While loading, show a loading row instead of the empty message (avoids paid-list flicker).
    isLoadingPaid?: boolean;
    // שולם toggle: independent 4th chip; the three type chips stay mutually exclusive.
    paidActive?: boolean;
    onTogglePaid?: () => void;
    // Per-row חשבון quick bill preview; parent owns the modal/fetch (Rule 32).
    onPreviewBill?: (orderId: number) => void;
    // Per-row קבלה receipt preview shown only in paid mode; parent owns the modal/fetch.
    onPreviewReceipt?: (orderId: number) => void;
};

// Architectural summary: Orders table UI for the POS orders tab (details in function library docs).
const OrdersTableComponent: React.FC<OrdersTableProps> = ({
    orders,
    selectedOrderId = null,
    onSelectOrder,
    filterSlot,
    hideTypeFilter = false,
    searchPlaceholder = 'חיפוש לפי שולחן, מלצר או שם לקוח',
    emptyMessage = 'אין הזמנות פתוחות',
    isLoadingPaid = false,
    paidActive = false,
    onTogglePaid,
    onPreviewBill,
    onPreviewReceipt,
}) => {
    const [search, setSearch] = useState('');
    const [typeFilter, setTypeFilter] = useState<TypeFilterKey>('all');
    const [now, setNow] = useState(() => Date.now());
    const [mobileActionOrder, setMobileActionOrder] = useState<OrderView | null>(null);
    const isMobile = useMediaQuery('(max-width: 767px)');

    // Architectural summary: Refreshes elapsed-time columns every 30s.
    useEffect(() => {
        return subscribeSharedTicker(30_000, () => setNow(Date.now()));
    }, []);

    const filteredOrders = useMemo(() => {
        const normalized = search.trim().toLowerCase();
        return orders.filter((order) => {
            const isQuickCharge = isQuickChargeOrderCode(order.orderCode);
            if (typeFilter === 'quick_charge' && !isQuickCharge) {
                return false;
            }
            if (typeFilter === 'take_away' && (order.orderType !== 'take_away' || isQuickCharge)) {
                return false;
            }
            if ((typeFilter === 'dine_in') && order.orderType !== typeFilter) {
                return false;
            }
            if (!normalized) {
                return true;
            }
            const tableMatch = String(order.tableNumber ?? '').toLowerCase().includes(normalized);
            const codeMatch = order.orderCode.toLowerCase().includes(normalized);
            const guestMatch = (order.guestName ?? '').toLowerCase().includes(normalized);
            return tableMatch || codeMatch || guestMatch;
        });
    }, [orders, search, typeFilter]);

    const typeOptions: Array<{ key: TypeFilterKey; label: string }> = [
        { key: 'all', label: 'הכל' },
        { key: 'dine_in', label: 'רגיל' },
        { key: 'take_away', label: 'לקחת' },
        { key: 'quick_charge', label: 'חיוב מהיר' },
    ];

    const totalDiners = useMemo(
        () => filteredOrders.reduce((sum, order) => sum + Math.max(0, Number(order.dinersCount) || 0), 0),
        [filteredOrders],
    );

    const totalIncludingTips = useMemo(
        () => filteredOrders.reduce((sum, order) => {
            const orderTotal = Number(order.totalAmount) || 0;
            return sum + orderTotal + resolveTip(order.tipInputMode ?? null, order.tipInputValue ?? null, orderTotal);
        }, 0),
        [filteredOrders],
    );

    const formatMinutes = (iso: string) => {
        const minutes = Math.max(0, Math.floor((now - new Date(iso).getTime()) / 60000));
        if (minutes < 1) {
            return 'עכשיו';
        }
        if (minutes < 60) {
            return `${minutes} דק׳`;
        }
        const hours = Math.floor(minutes / 60);
        const remainder = minutes % 60;
        return `${hours}:${remainder.toString().padStart(2, '0')} שע׳`;
    };

    const formatAveragePerDiner = (order: OrderView) => {
        const diners = Math.max(1, Number(order.dinersCount) || 0);
        return formatMoney((Number(order.totalAmount) || 0) / diners);
    };

    const handleRowClick = (order: OrderView) => {
        if (isMobile) {
            setMobileActionOrder(order);
            return;
        }
        onSelectOrder(order);
    };

    const handleOpenMobileOrder = () => {
        if (!mobileActionOrder) return;
        const order = mobileActionOrder;
        setMobileActionOrder(null);
        onSelectOrder(order);
    };

    const handlePreviewMobileBill = () => {
        if (!mobileActionOrder || !onPreviewBill) return;
        const orderId = mobileActionOrder.id;
        setMobileActionOrder(null);
        onPreviewBill(orderId);
    };

    const handlePreviewMobileReceipt = () => {
        if (!mobileActionOrder || !onPreviewReceipt) return;
        const orderId = mobileActionOrder.id;
        setMobileActionOrder(null);
        onPreviewReceipt(orderId);
    };

    return (
        <div className={classes.container}>
            {!hideTypeFilter && (
                <div className={classes.typeBanner} role="group" aria-label="סוג הזמנה">
                    {typeOptions.map((option) => (
                        <Button
                            key={option.key}
                            unstyled
                            type="button"
                            data-testid={`orders-table-type-${option.key}`}
                            aria-pressed={typeFilter === option.key}
                            className={`${classes.typeTab} ${typeFilter === option.key ? classes.typeTabActive : ''}`}
                            onClick={() => setTypeFilter(option.key)}
                        >
                            {option.label}
                        </Button>
                    ))}
                    {onTogglePaid && (
                        <Button
                            unstyled
                            type="button"
                            aria-pressed={paidActive}
                            className={`${classes.typeTab} ${paidActive ? classes.typeTabPaid : ''}`}
                            data-testid="orders-table-paid-toggle"
                            onClick={onTogglePaid}
                        >
                            שולם
                        </Button>
                    )}
                </div>
            )}
            <div className={classes.searchRow}>
                <TextInput
                    value={search}
                    onChange={(event) => setSearch(event.currentTarget.value)}
                    placeholder={searchPlaceholder}
                    data-testid="orders-table-search"
                    classNames={{ input: classes.searchInput }}
                    leftSection={<FontAwesomeIcon icon={faMagnifyingGlass as IconProp} className={classes.muted} />}
                    radius="md"
                    w="100%"
                />
            </div>

            {filterSlot}

            <div className={classes.tableWrapper}>
                <div className={classes.mobileHeader} aria-hidden="true">
                    <div className={classes.mobileMetaHeader}>
                        <span>נפתח</span>
                        <span>ממוצע</span>
                        <span>מחיר</span>
                    </div>
                    <span className={classes.mobileTableHeader}>שולחן</span>
                    <div className={classes.mobileGuestHeader}>
                        <span>מלצר</span>
                        <span>סועדים</span>
                        <span>לקוח</span>
                    </div>
                </div>
                <table className={classes.table}>
                    <thead className={classes.thead}>
                        <tr>
                            <th>שולחן/מס׳ הזמנה</th>
                            <th>מלצר</th>
                            <th>סועדים</th>
                            <th>שם לקוח</th>
                            <th>סכום כולל</th>
                            <th>זמן פתיחה</th>
                            <th>ממוצע לסועד</th>
                            <th aria-hidden="true" />
                        </tr>
                    </thead>
                    <tbody>
                        {filteredOrders.map((order) => {
                            const isSelected = selectedOrderId === order.id;
                            return (
                                <tr
                                    key={order.id}
                                    className={`${classes.row} ${isSelected ? classes.rowSelected : ''} ${paidActive && !isMobile ? classes.rowStatic : ''}`}
                                    data-testid={`orders-table-row-${order.id}`}
                                    onClick={paidActive && !isMobile ? undefined : () => handleRowClick(order)}
                                >
                                    <td className={classes.tableCell} data-label={order.orderType === 'take_away' ? 'מס׳ הזמנה' : 'שולחן'}>
                                        <span className={`${classes.tableNumber}${order.tableNumber == null ? ` ${classes.tableNumberCode}` : ''}`}>{order.tableNumber ?? order.orderCode}</span>
                                    </td>
                                    <td data-label="מלצר">{order.openedByName ?? '—'}</td>
                                    <td className={classes.dinersCell} data-label="סועדים">
                                        <img src="/diners.png" alt="" aria-hidden="true" />
                                        {order.dinersCount}
                                    </td>
                                    <td
                                        className={order.guestName ? undefined : classes.muted}
                                        data-label="שם לקוח"
                                    >
                                        {order.guestName ?? (order.orderType === 'take_away' ? order.orderCode : '—')}
                                    </td>
                                    <td data-label="סכום כולל" className={classes.amountCell}>{formatMoney(order.totalAmount ?? 0)} ₪</td>
                                    <td className={classes.openedCell} data-label="נפתח">
                                                                            <span className={classes.openedFolder} aria-hidden="true">📂</span>
                                        <span>{formatMinutes(order.createdAt)}</span>
                                    </td>
                                    <td className={classes.averageCell} data-label="ממוצע לסועד">
                                        <span>₪ {formatAveragePerDiner(order)}</span>
                                    </td>
                                    <td className={classes.billActionCell}>
                                        {onPreviewBill && (
                                            <UnstyledButton
                                                component="button"
                                                type="button"
                                                className={classes.billPreviewButton}
                                                data-testid={`orders-table-bill-preview-${order.id}`}
                                                aria-label={`חשבון ${order.tableNumber ?? order.orderCode}`}
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    onPreviewBill(order.id);
                                                }}
                                            >
                                                <FontAwesomeIcon icon={faFileLines as IconProp} className={classes.billPreviewIcon} />
                                                <span className={classes.billPreviewCaption}>חשבון</span>
                                            </UnstyledButton>
                                        )}
                                        {paidActive && onPreviewReceipt && (
                                            <UnstyledButton
                                                component="button"
                                                type="button"
                                                className={classes.billPreviewButton}
                                                data-testid={`orders-table-receipt-preview-${order.id}`}
                                                aria-label={`קבלה ${order.tableNumber ?? order.orderCode}`}
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                    onPreviewReceipt(order.id);
                                                }}
                                            >
                                                <FontAwesomeIcon icon={faFileLines as IconProp} className={classes.billPreviewIcon} />
                                                <span className={classes.billPreviewCaption}>קבלה</span>
                                            </UnstyledButton>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                        {filteredOrders.length === 0 && (
                            <tr className={classes.emptyRow}>
                                <td colSpan={8} className={classes.empty}>
                                    {isLoadingPaid
                                        ? 'טוען...'
                                        : orders.length === 0 ? emptyMessage : 'לא נמצאו תוצאות'}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            <Modal
                opened={mobileActionOrder !== null}
                onClose={() => setMobileActionOrder(null)}
                title={mobileActionOrder && (mobileActionOrder.tableNumber === null ? `הזמנה ${mobileActionOrder.orderCode}` : `שולחן ${mobileActionOrder.tableNumber}`)}
                centered
                radius="md"
                size="sm"
                dir="rtl"
                classNames={{
                    content: classes.mobileOrderActionContent,
                    header: classes.mobileOrderActionHeader,
                    title: classes.mobileOrderActionTitle,
                    body: classes.mobileOrderActionBody,
                }}
                closeButtonProps={{ 'data-testid': 'orders-table-mobile-action-close' } as Record<string, string>}
            >
                {mobileActionOrder && (
                    <div className={classes.mobileOrderActionModal}>
                        <p className={classes.mobileOrderActionSummary}>
                            {mobileActionOrder.dinersCount} אורחים · {formatMoney(mobileActionOrder.totalAmount ?? 0)} ₪
                        </p>
                        <div className={classes.mobileOrderActionList}>
                            {paidActive ? onPreviewReceipt && (
                                <UnstyledButton
                                    component="button"
                                    type="button"
                                    className={`${classes.mobileOrderAction} ${classes.mobileOrderActionPrimary}`}
                                    data-testid="orders-table-mobile-action-receipt"
                                    onClick={handlePreviewMobileReceipt}
                                >
                                    <span className={`${classes.mobileOrderActionIcon} ${classes.mobileOrderActionIconBill}`}>
                                        <FontAwesomeIcon icon={faFileLines as IconProp} />
                                    </span>
                                    <span className={classes.mobileOrderActionText}>
                                        <strong>הדפס קבלה</strong>
                                        <small>הצג והדפס קבלה</small>
                                    </span>
                                    <FontAwesomeIcon icon={faChevronLeft as IconProp} className={classes.mobileOrderActionChevron} />
                                </UnstyledButton>
                            ) : (
                                <>
                                    <UnstyledButton
                                        component="button"
                                        type="button"
                                        className={`${classes.mobileOrderAction} ${classes.mobileOrderActionPrimary}`}
                                        data-testid="orders-table-mobile-action-open"
                                        onClick={handleOpenMobileOrder}
                                    >
                                        <span className={`${classes.mobileOrderActionIcon} ${classes.mobileOrderActionIconPrimary}`}>
                                            <FontAwesomeIcon icon={faArrowRight as IconProp} />
                                        </span>
                                        <span className={classes.mobileOrderActionText}>
                                            <strong>כניסה להזמנה</strong>
                                            <small>פתח ועדכן את ההזמנה הנוכחית</small>
                                        </span>
                                        <FontAwesomeIcon icon={faChevronLeft as IconProp} className={classes.mobileOrderActionChevron} />
                                    </UnstyledButton>
                                    {onPreviewBill && (
                                <UnstyledButton
                                    component="button"
                                    type="button"
                                    className={classes.mobileOrderAction}
                                    data-testid="orders-table-mobile-action-bill"
                                    onClick={handlePreviewMobileBill}
                                >
                                    <span className={`${classes.mobileOrderActionIcon} ${classes.mobileOrderActionIconBill}`}>
                                        <FontAwesomeIcon icon={faFileLines as IconProp} />
                                    </span>
                                    <span className={classes.mobileOrderActionText}>
                                        <strong>הצג חשבון</strong>
                                        <small>הצג/הדפס חשבון</small>
                                    </span>
                                    <FontAwesomeIcon icon={faChevronLeft as IconProp} className={classes.mobileOrderActionChevron} />
                                </UnstyledButton>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                )}
            </Modal>

            <div className={classes.footerBar}>
                <div className={classes.footerDesktopSummary}>
                    <span>מספר סועדים: {totalDiners}</span>
                    <span>סך הכל הזמנות: {filteredOrders.length}</span>
                    <span>סה״כ (כולל טיפים): {formatMoney(totalIncludingTips)} ₪</span>
                </div>
                <div className={classes.footerMobileSummary}>
                    <div className={classes.footerSummaryRow}>
                        <span className={classes.footerSummaryLabel}>מספר סועדים:</span>
                        <span className={classes.footerSummaryValue}>{totalDiners}</span>
                    </div>
                    <div className={classes.footerSummaryRow}>
                        <span className={classes.footerSummaryLabel}>סך הכל הזמנות:</span>
                        <span className={classes.footerSummaryValue}>{filteredOrders.length}</span>
                    </div>
                    <div className={classes.footerSummaryRow}>
                        <span className={classes.footerSummaryLabel}>סה״כ (כולל טיפים):</span>
                        <span className={classes.footerSummaryValue}>{formatMoney(totalIncludingTips)} ₪</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export const OrdersTable = React.memo(OrdersTableComponent);
